'use client';

import React, { useRef, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import ChatMessage from './ChatMessage';

interface ConversationMessage {
  id?: number; // 数据库主键ID
  role: 'user' | 'assistant' | 'error';
  content: string;
  timestamp?: string;
  engines_used?: any[];
  is_error?: boolean;
}

interface Conversations {
  [modelId: string]: ConversationMessage[];
}

interface LoadingState {
  [modelId: string]: boolean;
}

interface ChatAreaProps {
  selectedModels: string[];
  conversations: Conversations;
  loading: LoadingState;
  getModelName: (modelId: string) => string;
  onClearConversation: () => void;
  bookmarkedMessages?: Set<string>;
  onToggleBookmark?: (messageId: string) => void;
  onSaveFact?: (query: string, response: string) => void;
}

const ChatArea: React.FC<ChatAreaProps> = ({
  selectedModels,
  conversations,
  loading,
  getModelName,
  onClearConversation,
  bookmarkedMessages = new Set(),
  onToggleBookmark,
  onSaveFact
}) => {
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // 自动滚动到底部
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [conversations, loading]);

  if (selectedModels.length === 0) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'text.secondary',
          textAlign: 'center'
        }}
      >
        <Typography variant="h6" gutterBottom>
          欢迎使用AI智能对话
        </Typography>
        <Typography variant="body2">
          请选择一个模型开始对话
        </Typography>
      </Box>
    );
  }

  // 使用第一个选中的模型进行对话
  const modelId = selectedModels[0];
  const modelConversation = conversations[modelId] || [];
  // 处理loading状态：loading可能是boolean或object
  const isLoading = typeof loading === 'boolean' ? loading : (loading && loading[modelId]);

  return (
    <Box
      ref={chatContainerRef}
      sx={{
        height: '100%',
        overflowY: 'auto',
        p: 2,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }
      }}
    >
      {modelConversation.length === 0 ? (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: 4,
            py: 6,
            maxWidth: '700px',
            mx: 'auto'
          }}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 600, 
              mb: 4,
              color: 'text.primary',
              textAlign: 'center'
            }}
          >
            我可以：
          </Typography>
          
          <Box sx={{ width: '100%', mb: 3.5 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 1.5, 
                fontWeight: 500,
                color: 'text.primary',
                fontSize: '15px'
              }}
            >
              从网络搜集资料完成深度调研，你可以问：
            </Typography>
            <Box 
              sx={{ 
                bgcolor: 'grey.50',
                borderLeft: '3px solid',
                borderColor: 'primary.main',
                pl: 2.5,
                py: 1.5,
                borderRadius: '4px',
                ml: 1
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  lineHeight: 1.7,
                  fontSize: '14px'
                }}
              >
                搜索网络寻找与当前项目类似的案例，整理此类项目最容易出现的问题清单。
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ width: '100%', mb: 3.5 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 1.5, 
                fontWeight: 500,
                color: 'text.primary',
                fontSize: '15px'
              }}
            >
              结合当前项目已有资料进行深度洞察，你可以问：
            </Typography>
            <Box 
              sx={{ 
                bgcolor: 'grey.50',
                borderLeft: '3px solid',
                borderColor: 'primary.main',
                pl: 2.5,
                py: 1.5,
                borderRadius: '4px',
                ml: 1
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  lineHeight: 1.7,
                  fontSize: '14px'
                }}
              >
                根据当前项目的XXX问题，给出整改建议。
              </Typography>
            </Box>
          </Box>
          
          <Box 
            sx={{ 
              width: '100%',
              mt: 2,
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                textAlign: 'center',
                fontSize: '13px',
                lineHeight: 1.6
              }}
            >
              上述回复你可以保存为日志用于后续指标设计，章节写作。
            </Typography>
          </Box>
        </Box>
      ) : (
        modelConversation.map((message, msgIndex) => {
          // 优先使用数据库ID，如果没有则使用生成的ID
          const messageId = message.id ? message.id.toString() : `${message.timestamp || `msg-${msgIndex}`}-${msgIndex}`;
          const isBookmarked = bookmarkedMessages.has(messageId);

          return (
            <Box
              key={messageId}
              id={`message-${messageId}`}
              sx={{ mb: 2 }}
            >
              <ChatMessage
                message={message}
                showTimestamp={msgIndex === modelConversation.length - 1}
                messageId={messageId}
                isBookmarked={isBookmarked}
                onToggleBookmark={onToggleBookmark ? (msgId) => {
                  onToggleBookmark(msgId || messageId);
                } : undefined}
                onSaveFact={onSaveFact ? (content) => {
                  // Find the previous message to use as the query
                  const prevMsg = msgIndex > 0 ? modelConversation[msgIndex - 1] : null;
                  const query = prevMsg && prevMsg.role === 'user' ? prevMsg.content : 'User Query';
                  onSaveFact(query, content);
                } : undefined}
              />
            </Box>
          );
        })
      )}

      {/* 加载状态指示器 - AI正在处理时的消息气泡 */}
      {isLoading && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            {/* AI头像 */}
            <Box sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '14px',
              fontWeight: 'bold'
            }}>
              AI
            </Box>

            {/* AI正在处理的提示 */}
            <Box sx={{
              bgcolor: 'grey.50',
              border: 1,
              borderColor: 'grey.200',
              borderRadius: 2,
              p: 2,
              maxWidth: '70%',
              position: 'relative'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  AI正在思考中...
                </Typography>
              </Box>

              {/* 跳动的省略号动画 */}
              <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                <Box sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  animation: 'pulse 1.4s infinite ease-in-out both'
                }} />
                <Box sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  animation: 'pulse 1.4s infinite ease-in-out both',
                  animationDelay: '0.16s'
                }} />
                <Box sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  animation: 'pulse 1.4s infinite ease-in-out both',
                  animationDelay: '0.32s'
                }} />
              </Box>
            </Box>
          </Box>

          {/* 添加CSS动画 */}
          <style>{`
            @keyframes pulse {
              0%, 80%, 100% {
                transform: scale(0);
                opacity: 0.5;
              }
              40% {
                transform: scale(1);
                opacity: 1;
              }
            }
          `}</style>
        </Box>
      )}
    </Box>
  );
};

export default ChatArea;